export interface JwtPayload {
	exp?: number
	iat?: number
	[key: string]: unknown
}

/**
 * Safely parses a JWT string to extract its payload object.
 * Returns null if token is invalid, malformed, or missing.
 */
export function parseJwt(token: string | null | undefined): JwtPayload | null {
	if (!token || typeof token !== 'string') return null
	const parts = token.split('.')
	if (parts.length !== 3) return null

	try {
		const base64Url = parts[1]
		let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
		const padLength = (4 - (base64.length % 4)) % 4
		base64 += '='.repeat(padLength)

		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join(''),
		)
		return JSON.parse(jsonPayload) as JwtPayload
	} catch {
		return null
	}
}

/**
 * Returns the expiration Date of a JWT, or null if no valid 'exp' claim exists.
 */
export function getTokenExpirationDate(token: string | null | undefined): Date | null {
	const payload = parseJwt(token)
	if (!payload || typeof payload.exp !== 'number' || isNaN(payload.exp)) return null
	return new Date(payload.exp * 1000)
}

/**
 * Returns remaining milliseconds until token expires.
 * Returns 0 if token is invalid, expired, or has no exp claim.
 */
export function getTokenRemainingTimeMs(token: string | null | undefined): number {
	const expiration = getTokenExpirationDate(token)
	if (!expiration) return 0
	return Math.max(0, expiration.getTime() - Date.now())
}

/**
 * Checks if the token is expired or expiring within bufferSeconds.
 * If token is invalid or missing, returns true.
 * If token has no exp claim (non-JWT), returns false.
 */
export function isTokenExpiringSoon(token: string | null | undefined, bufferSeconds = 120): boolean {
	if (!token) return true
	const expiration = getTokenExpirationDate(token)
	if (!expiration) return false
	const remainingMs = expiration.getTime() - Date.now()
	return remainingMs <= bufferSeconds * 1000
}
